import { useState, useMemo, useEffect } from 'react';
import { X } from 'lucide-react';
import { FlowCard } from '@Components/FlowCategoryDetails/FlowCard';
import { useFlowCategory } from '@Hooks/useFlowCategories';
import { useParams } from 'react-router-dom';
import { toastError } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import Breadcrumbs from '@Components/Breadcrumbs';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';

export function FlowCategoryDetails() {
  const { id } = useParams();
  const [titleFilter, setTitleFilter] = useState('');

  const {
    data: payload,
    isError,
    error,
  } = useFlowCategory({
    id,
    searchParams: { include: 'flows' },
    enabled: true,
  });

  const filteredFlows = useMemo(() => {
    if (!payload?.flows) return [];
    if (!titleFilter.trim()) return payload.flows;
    return payload.flows.filter((flow) =>
      flow.flowTitle?.toLowerCase().includes(titleFilter.toLowerCase()),
    );
  }, [payload?.flows, titleFilter]);

  useEffect(() => {
    if (isError && error) {
      toastError(
        error instanceof Error
          ? error.message
          : 'Failed to load Flow Categories',
      );
    }
  }, [isError, error]);

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs
        navList={[
          { name: 'Flows', href: '/flows' },
          { name: 'Cards', href: '/flows/cards' },
          { name: 'Category Details', href: '/flows/cards/*' },
        ]}
      />

      <div>
        <Heading>Flow Category Details</Heading>
        <p>View the Flows within a Category.</p>
      </div>

      <div className="relative w-64">
        <Input
          type="text"
          placeholder="Filter by flow title..."
          value={titleFilter}
          onChange={(e) => setTitleFilter(e.target.value)}
          className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 pr-8 text-sm text-zinc-800 placeholder:text-zinc-400 focus:ring-2 focus:ring-zinc-300 focus:outline-none"
        />
        {titleFilter && (
          <button
            type="button"
            onClick={() => setTitleFilter('')}
            className="absolute top-1/2 right-2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      <div className="flex flex-col gap-4">
        {filteredFlows.map((flow) => (
          <FlowCard key={flow.id} flow={flow} />
        ))}

        {filteredFlows.length === 0 && (
          <div className="flex items-center justify-center rounded-xl border border-dashed border-zinc-300 py-16 text-sm text-zinc-500">
            No flows match the current filters.
          </div>
        )}
      </div>
    </div>
  );
}
